import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { useCreateUser } from '../hooks/use-users'
import { buildCreateUserSchema } from '../types'
import type { CreateUserFormData } from '../types'
import type { Role } from '@/shared/constants/roles'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { ROLES } from '@/shared/constants/roles'
import { useAuth } from '@/features/auth/hooks/use-auth'

export function UserCreatePage() {
  const navigate = useNavigate()
  const { user, hasRole } = useAuth()
  const { t: tUsers } = useTranslation('users')
  const { t: tCommon } = useTranslation('common')
  const [tenantIdInput, setTenantIdInput] = useState(
    user?.tenantId?.toString() ?? '',
  )

  const tenantId = useMemo(() => {
    if (user?.tenantId) return user.tenantId
    const parsed = Number(tenantIdInput)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }, [user?.tenantId, tenantIdInput])

  const canAssignSuperAdmin = hasRole(ROLES.SUPER_ADMIN)
  const availableRoles = (Object.values(ROLES) as Array<Role>).filter(
    (role) => canAssignSuperAdmin || role !== ROLES.SUPER_ADMIN,
  )

  const createUser = useCreateUser(tenantId)

  const createUserSchema = useMemo(
    () => buildCreateUserSchema(tUsers),
    [tUsers],
  )

  const form = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      email: '',
      password: '',
      roles: [],
      firstName: '',
      lastName: '',
    },
  })

  const roles = form.watch('roles')

  function toggleRole(role: Role, checked: boolean) {
    const current = new Set(roles)
    if (checked) current.add(role)
    else current.delete(role)
    form.setValue('roles', Array.from(current), { shouldValidate: true })
  }

  const onSubmit = (data: CreateUserFormData) => {
    if (!tenantId) return
    createUser.mutate(data, {
      onSuccess: (result) => {
        navigate({ to: `/users/${result.id}` })
      },
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{tUsers('create.title')}</h1>
        <p className="text-muted-foreground mt-1">
          {tUsers('create.subtitle')}
        </p>
      </div>

      {!tenantId && canAssignSuperAdmin && !user?.tenantId && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{tUsers('create.tenantRequiredTitle')}</AlertTitle>
          <AlertDescription>
            {tUsers('create.tenantRequiredDescription')}
          </AlertDescription>
        </Alert>
      )}

      {canAssignSuperAdmin && !user?.tenantId && (
        <div className="max-w-xs">
          <Label htmlFor="tenant-id">{tUsers('create.tenantIdLabel')}</Label>
          <Input
            id="tenant-id"
            type="number"
            value={tenantIdInput}
            onChange={(event) => setTenantIdInput(event.target.value)}
            placeholder={tUsers('create.tenantIdPlaceholder')}
            className="mt-1.5"
          />
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{tUsers('create.detailsTitle')}</CardTitle>
          <CardDescription>
            {tUsers('create.detailsDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{tUsers('create.emailLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder={tUsers('create.emailPlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{tUsers('create.passwordLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder={tUsers('create.passwordPlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div>
                <Label>{tUsers('create.rolesLabel')}</Label>
                <div className="mt-2 space-y-2">
                  {availableRoles.map((role) => (
                    <div key={role} className="flex items-center gap-2">
                      <Checkbox
                        checked={roles.includes(role)}
                        onCheckedChange={(checked) =>
                          toggleRole(role, Boolean(checked))
                        }
                      />
                      <span className="text-sm">{role.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>
                <FormField
                  control={form.control}
                  name="roles"
                  render={() => (
                    <FormItem>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{tUsers('create.firstNameLabel')}</FormLabel>
                      <FormControl>
                        <Input placeholder={tCommon('optional')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{tUsers('create.lastNameLabel')}</FormLabel>
                      <FormControl>
                        <Input placeholder={tCommon('optional')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate({ to: '/users' })}
                >
                  {tCommon('cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={createUser.isPending || !tenantId}
                >
                  {createUser.isPending
                    ? tCommon('creating')
                    : tUsers('create.createAction')}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}
